import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionPlanService } from "../api/ActionPlanService";
import { DiversityRatioService } from "../api/DiversityRatioService";
import { PipelineService } from "../api/PipelineService";
import { SurveyService } from "../api/SurveyService";
import { WorkplaceCultureService } from "../api/WorkplaceCultureService";
import { ActionPlanSetupBanner } from "../components/ActionPlanSetupBanner/ActionPlanSetupBanner";
import { ActionPlanTodoWidget } from "../components/ActionPlanTodoWidget/ActionPlanTodoWidget";
import { ActionPlanWidget } from "../components/ActionPlanWidget/ActionPlanWidget";
import { DiversityRatioWidget } from "../components/DiversityRatioWidget/DiversityRatioWidget";
import { InsightsWidget } from "../components/InsightsWidget/InsightsWidget";
import { PipelineWidget } from "../components/PipelineWidget/PipelineWidget";
import { WorkplaceCultureWidget } from "../components/WorkplaceCultureWidget/WorkplaceCultureWidget";
import { Typography } from "../components/ui/Typography/Typography";
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import useTranslation from "../hooks/useTranslation";
import { Activity } from "../models/activity";
import SurveyDispatch from "../models/surveyDispatch";
import SurveyDispatchManager from "../models/surveyDispatchManager";
import { ActivityResponse } from "../types/actionPlan";
import {
  ErrorCodes as DiversityRatioErrorCodes,
  GetDiversityRatioResponse,
} from "../types/diversityRatio";
import { ErrorResponse } from "../types/error";
import {
  ErrorCodes as PipelineErrorCodes,
  PipelineResponse,
} from "../types/pipeline";
import {
  GetSurveyDispatchResponse,
  GetSurveyDispatchesResponse,
  InvalidRespondentsResponse,
} from "../types/survey";
import { SubscriptionActions } from "../types/user";
import {
  GetSurveyChapterInsightResponse,
  GetSurveyChapterScoringResponse,
  GetSurveyScoringDemographicResponse,
} from "../types/workplaceCulture";
import { ProtectedRoutes } from "../utils/routes.utils";
import "./DashboardPage.scss";

export const DashboardPage = () => {
  const { t } = useTranslation("dashboardPage");
  const { t: tWorkplaceCulture } = useTranslation("workplaceCulturePage");

  const { authInfo } = useAuth();
  const { hasAccess } = useSubscription();

  const [latestDispatch, setLatestSurveyDispatch] = useState<SurveyDispatch>();
  const [manager, setManager] = useState<SurveyDispatchManager>();
  const [latestDispatchId, setLatestDispatchId] = useState<string>();
  const [pipelineErrorCode, setPipelineErrorCode] = useState<string>("");
  const [diversityRatioErrorCode, setDiversityRatioErrorCode] =
    useState<string>("");
  const [activities, setActivities] = useState<Activity[]>([]);

  const navigate = useNavigate();

  const actionPlanService = new ActionPlanService();
  const surveyService = new SurveyService();
  const workplaceCultureService = new WorkplaceCultureService();
  const diversityRatioService = new DiversityRatioService();
  const pipelineService = new PipelineService();

  const {
    data: actionPlanData,
    isLoading: actionPlanLoading,
    error: actionPlanError,
    refetch: handleActionPlanRefetch,
  } = useQuery<ActivityResponse[], AxiosError>(["actionPlan"], () =>
    actionPlanService.getActivities()
  );

  const {
    data: surveyDispatchesData,
    isLoading: surveyDispatchesLoading,
    error: surveyDispatchesError,
  } = useQuery<GetSurveyDispatchesResponse[], AxiosError>(
    ["surveyDispatches"],
    () => surveyService.getDispatches()
  );

  const { data: latestDispatchData, refetch: handleSurveyDispatchRefresh } =
    useQuery<GetSurveyDispatchResponse, AxiosError>(
      ["latestDispatch"],
      () => {
        if (!latestDispatchId) throw new Error("Dispatch ID is required");
        return surveyService.getDispatch(latestDispatchId);
      },
      {
        enabled: !!latestDispatchId,
      }
    );

  const {
    data: surveyScoringData,
    isLoading: surveyScoringLoading,
    error: surveyScoringError,
  } = useQuery<GetSurveyScoringDemographicResponse, AxiosError>(
    [`surveyScoring-${latestDispatch?.id}`],
    () => workplaceCultureService.getSurveyScoring(latestDispatch?.id || ""),
    {
      enabled: !!latestDispatch?.id,
    }
  );

  const {
    data: surveyChapterScoringData,
    isLoading: surveyChapterScoringLoading,
    error: surveyChapterScoringError,
  } = useQuery<GetSurveyChapterScoringResponse, AxiosError>(
    [`surveyChapterScoring-${latestDispatch?.id}`],
    () =>
      workplaceCultureService.getSurveyChapterScoring(latestDispatch?.id || ""),
    {
      enabled: !!latestDispatch?.id,
    }
  );

  const {
    data: surveyChapterInsightsData,
    isLoading: surveyChapterInsightsLoading,
    error: surveyChapterInsightsError,
  } = useQuery<GetSurveyChapterInsightResponse, AxiosError>(
    [`surveyChapterInsights-${latestDispatch?.id}`],
    () =>
      workplaceCultureService.getSurveyChapterInsights(
        latestDispatch?.id || ""
      ),
    {
      enabled: !!latestDispatch?.id,
    }
  );

  const { data: invalidRespondentsData, error: invalidRespondentsError } =
    useQuery<InvalidRespondentsResponse, AxiosError>(
      [`surveyInvalidRespondents-${latestDispatch?.id}`],
      () => {
        if (!latestDispatch) throw new Error("Dispatch is required");
        return surveyService.getInvalidRespondents(latestDispatch);
      },
      {
        enabled: !!latestDispatch?.id,
      }
    );

  const {
    data: diversityRatioData,
    isLoading: diversityRatioLoading,
    error: diversityRatioError,
  } = useQuery<GetDiversityRatioResponse, AxiosError>(
    ["diversityRatio"],
    () => diversityRatioService.getDiversityRatio(),
    { cacheTime: 60 * 60 * 1000 } // increase cache time to 60 minutes
  );

  const {
    data: pipelineData,
    isLoading: pipelineDataLoading,
    error: pipelineDataError,
  } = useQuery<PipelineResponse, AxiosError>(["pipeline"], () =>
    pipelineService.getPipeline()
  );

  useEffect(() => {
    const errorData = pipelineDataError?.response
      ?.data as ErrorResponse<PipelineErrorCodes>;
    setPipelineErrorCode(errorData?.extra?.code || "");
  }, [pipelineDataError]);

  useEffect(() => {
    const errorData = diversityRatioError?.response
      ?.data as ErrorResponse<DiversityRatioErrorCodes>;
    setDiversityRatioErrorCode(errorData?.extra?.code || "");
  }, [diversityRatioError]);

  useEffect(() => {
    if (surveyDispatchesData) {
      const newManager = new SurveyDispatchManager(
        surveyDispatchesData,
        tWorkplaceCulture
      );
      setManager(newManager);
      setLatestDispatchId(newManager.getLatestId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyDispatchesData]);

  useEffect(() => {
    if (latestDispatchData) {
      const dispatch = new SurveyDispatch(latestDispatchData);
      setLatestSurveyDispatch(dispatch);
    }
  }, [latestDispatchData]);

  useEffect(() => {
    if (actionPlanData) {
      const activities = actionPlanData.map(
        (activity) => new Activity(activity)
      );
      setActivities(activities);
    }
  }, [actionPlanData]);

  const handleActionPlanSetupClick = () => {
    navigate(`${ProtectedRoutes.ACTION_PLAN}?skipIntro=true`);
  };

  const getNow = () => {
    if (!hasAccess(SubscriptionActions.ACTION_PLAN)) {
      return new Date("2024-11-15");
    } else if (import.meta.env.VITE_CURRENT_DATE) {
      return new Date(import.meta.env.VITE_CURRENT_DATE);
    }
    return new Date();
  };
  const now = getNow();

  return (
    <div className="DashboardPage">
      <Typography
        className="DashboardPage__title"
        tagVariant="h1"
        desktop="h1"
        data-testid="dashboard-title"
      >
        {t("title")}
        <Typography tagVariant="span" desktop="handwrittenH1" color="error">
          {authInfo.user?.getCompanyName()}?
        </Typography>
      </Typography>
      <Grid container spacing={3} marginBottom={3}>
        {import.meta.env.VITE_FEATURE_ACTION_PLAN === "true" && (
          <Grid
            item
            md={import.meta.env.VITE_FEATURE_TODO === "true" ? 8 : 12}
            className="DashboardPage__widget"
          >
            {!actionPlanLoading &&
            activities &&
            hasAccess(SubscriptionActions.ACTION_PLAN) &&
            (activities.length === 0 ||
              !activities.some((activity) => activity.isTrainingActivity())) ? (
              <ActionPlanSetupBanner
                title={t("actionPlanWidget.setupBanner.title")}
                description={t("actionPlanWidget.setupBanner.description")}
                buttonLabel={t("actionPlanWidget.setupBanner.buttonLabel")}
                onClick={handleActionPlanSetupClick}
              />
            ) : (
              <ActionPlanWidget
                dispatchManager={manager}
                activities={activities}
                error={!!actionPlanError}
                isLoading={actionPlanLoading}
                onActivitiesRefetch={handleActionPlanRefetch}
                now={now}
                preview={!hasAccess(SubscriptionActions.ACTION_PLAN)}
                hasSurveyAccess={hasAccess(SubscriptionActions.LAUNCH_SURVEY)}
              />
            )}
          </Grid>
        )}
        {import.meta.env.VITE_FEATURE_TODO === "true" && (
          <Grid item md={4} className="DashboardPage__widget">
            <ActionPlanTodoWidget />
          </Grid>
        )}
        <Grid item lg={8} className="DashboardPage__widget">
          <WorkplaceCultureWidget
            surveyDispatch={latestDispatch}
            surveyDispatchError={!!surveyDispatchesError}
            surveyDispatchLoading={surveyDispatchesLoading}
            onSurveyUpdate={handleSurveyDispatchRefresh}
            scoringData={surveyScoringData}
            scoringError={!!surveyScoringError || !!surveyChapterScoringError}
            scoringIsLoading={
              surveyScoringLoading || surveyChapterScoringLoading
            }
            preview={
              !hasAccess(SubscriptionActions.LAUNCH_SURVEY) &&
              surveyDispatchesData?.length === 0
            }
            chapters={surveyChapterScoringData?.chapters}
            hasDispatch={
              !!surveyDispatchesData ? surveyDispatchesData?.length > 0 : false
            }
            hasOlderDispatches={
              !!surveyDispatchesData ? surveyDispatchesData.length > 1 : false
            }
            invalidRespondents={invalidRespondentsData}
            invalidRespondentsError={!!invalidRespondentsError}
          />
        </Grid>
        <Grid item sm={12} md={6} lg={4} className="DashboardPage__widget">
          <InsightsWidget
            insightsData={surveyChapterInsightsData}
            insightsLoading={
              (surveyChapterInsightsLoading && !!latestDispatch?.id) ||
              surveyDispatchesLoading
            }
            insightsError={!!surveyChapterInsightsError}
            noInsights={
              surveyChapterInsightsData?.length === 0 ||
              (!latestDispatch?.id && !surveyDispatchesLoading)
            }
            preview={!hasAccess(SubscriptionActions.LAUNCH_SURVEY)}
            surveyClosed={latestDispatch?.isClosed()}
            dispatchId={latestDispatch?.id || ""}
          />
        </Grid>
        {import.meta.env.VITE_HIDE_DIVERSITY_PIPELINE_WIDGETS !== "true" && (
          <Grid item sm={12} md={6} lg={4} className="DashboardPage__widget">
            <DiversityRatioWidget
              diversityData={diversityRatioData}
              diversityIsLoading={diversityRatioLoading}
              diversityError={!!diversityRatioError}
              noIntegration={
                diversityRatioErrorCode ===
                DiversityRatioErrorCodes.IntegrationNotFound
              }
              preview={diversityRatioError?.response?.status === 403}
            />
          </Grid>
        )}
        {import.meta.env.VITE_HIDE_DIVERSITY_PIPELINE_WIDGETS !== "true" && (
          <Grid item sm={12} md={12} lg={8} className="DashboardPage__widget">
            <PipelineWidget
              pipelineData={pipelineData}
              pipelineDataLoading={pipelineDataLoading}
              pipelineDataError={!!pipelineDataError}
              noIntegration={
                pipelineErrorCode === PipelineErrorCodes.IntegrationNotFound
              }
              noJobLevel={pipelineErrorCode === PipelineErrorCodes.NoJobLevel}
              preview={pipelineDataError?.response?.status === 403}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};
